import { inject, Injectable } from '@angular/core';
import { CrmEndpointDecorator } from 'common-module/endpoint';
import { CrmPaginated, CrmPagination } from 'common-module/table';
import { map, Observable, of } from 'rxjs';
import { groupBy } from 'lodash-es';
import { ImportDocumentType } from '@app/pages/imports/import-document-type';
import { Price } from '@app/core/model/price';
import { loadAllPaginated } from '@app/shared/helpers/load.helper';
import { CrmSafeAny } from 'common-module/core/types';

import { Product, ProductMedia } from '../model/product';
import { MediaService } from '../media/media.service';
import { MediaType } from '../media/media';

import { ProductEndpoint } from './product.endpoint';

const sortDesc = (a: string | number, b: string | number) =>
  Number(b) - Number(a);

export const commercialYearsSmallParams = {
  '$select[0]': 'name',
  '$select[1]': 'meta',
  '$sort[meta.period.startDate]': -1,
};

export const productMediaGetter =
  (mediaService: MediaService) =>
  (product: Product): Observable<[Product, ProductMedia[] | null]> => {
    if (!product.meta.identifiers?.materialCode) {
      console.warn(new Error('Missing materialCode in product identifiers'));
      return of([product, null]);
    }
    if (!product.meta.timing?.commercialYear) {
      console.warn(new Error('Missing commercialYear product timing'));
      return of([product, null]);
    }
    return mediaService
      .list({
        status: 'active',
        'meta.materialCode': product.meta.identifiers?.materialCode,
        type: MediaType.productImage,
        'meta.commercialYear[$lte]': product.meta.timing?.commercialYear,
        '$sort[meta.commercialYear]': -1,
        $limit: -1,
      })
      .pipe(
        map((media: ProductMedia[]) => {
          const returnValue = (
            mediaValue: ProductMedia[],
          ): [Product, ProductMedia[] | null] => [product, mediaValue];

          const productMedia = media.filter((m) => m.ref === product._id);
          if (productMedia.length) {
            return returnValue(productMedia);
          }
          const groupedMedia = groupBy(media, 'meta.commercialYear');
          const sortedCy = Object.keys(groupedMedia).sort(sortDesc);
          if (!sortedCy.length) {
            return returnValue([]);
          }
          const previousCommercialYear = sortedCy[0];
          const nextMedia = groupedMedia?.[previousCommercialYear] ?? [];
          return returnValue(nextMedia);
        }),
      );
  };

@Injectable({ providedIn: 'root' })
export class ProductService {
  @CrmEndpointDecorator({
    configName: 'crm',
    endpointName: 'products',
    endpoint: ProductEndpoint,
  })
  endpoint!: ProductEndpoint;
  private mediaService = inject(MediaService);

  listCommercialYears(params?: CrmSafeAny) {
    const opFactory: CrmSafeAny = (pag: CrmPagination) =>
      this.endpoint.list({
        params: {
          type: 'commercial_year',
          status: 'active',
          '$sort[meta.period.startDate]': -1,
          ...params,
          ...pag,
        },
      });

    return loadAllPaginated<Product>(opFactory, { pageSize: 50 }).pipe(
      map((res) => {
        return res.filter(
          (cy) => cy.meta?.period?.startDate && cy.meta?.period?.endDate,
        );
      }),
      map((res) =>
        res.sort(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          (a, b) =>
            new Date(a.meta!.period!.startDate).getTime() -
            new Date(b.meta!.period!.startDate).getTime(),
        ),
      ),
    ) as Observable<Product[]>;
  }

  search(term: string, params?: CrmSafeAny) {
    return this.endpoint.search(term, params);
  }

  listProducts(params?: CrmSafeAny) {
    return this.endpoint.list({
      params,
    });
  }

  get(id: string) {
    return this.endpoint.read(id);
  }

  update(id: string, body: CrmSafeAny) {
    return this.endpoint.update(id, body);
  }

  listMarkets() {
    const opFactory: (..._: CrmSafeAny) => Observable<CrmPaginated<Product>> = (
      pag: CrmPagination,
    ) =>
      this.listProducts({
        type: 'price_market',
        status: 'active',
        ...pag,
      });
    return loadAllPaginated(opFactory, { pageSize: 50 });
  }

  public importProduct(file: CrmSafeAny, documentType: ImportDocumentType) {
    const formData = new FormData();
    formData.append('file', file);
    return this.endpoint.request('POST', 'import', {
      body: formData,
      params: { type: documentType },
      headers: {
        Accept: '*/*',
      },
    });
  }

  public getCommercialYears(params?: CrmSafeAny): Observable<Product[]> {
    const opFactory = (pag: CrmPagination) =>
      this.endpoint.list({
        params: {
          type: 'commercial_year',
          status: 'active',
          ...pag,
          ...params,
        },
      }) as Observable<CrmPaginated<Product>>;

    return loadAllPaginated(opFactory, { pageSize: 50 }).pipe(
      map((res: Product[]) =>
        res.sort(
          (a, b) =>
            new Date(a.meta.period.startDate).getTime() -
            new Date(b.meta.period.startDate).getTime(),
        ),
      ),
    ) as Observable<Product[]>;
  }

  public listProductPrices(productId: string, params?: CrmSafeAny) {
    return this.endpoint.list({
      params: {
        ...params,
        type: 'price',
        parents: productId,
      },
    }) as Observable<CrmPaginated<Price>>;
  }
}
